import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { verifyAdminLogin } from "../../../api/api";
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Fade,
} from "@mui/material";
import { AdminPanelSettings as AdminIcon, CheckCircle as CheckIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components
const BackgroundBox = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
}));

const LoadingCard = styled(Box)(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 24,
    padding: '48px 64px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    maxWidth: 500,
    width: '90%',
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    animation: 'scaleIn 0.5s ease-out',
    '@keyframes scaleIn': {
        '0%': {
            transform: 'scale(0)',
            opacity: 0,
        },
        '100%': {
            transform: 'scale(1)',
            opacity: 1,
        },
    },
}));

const SpinnerBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'inline-block',
    marginBottom: 24,
}));

const VerifyMobile = () => {
    const navigate = useNavigate();
    const hasVerified = useRef(false);
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const verifyLogin = async () => {
            if (hasVerified.current) {
                return;
            }
            hasVerified.current = true;

            const queryParams = new URLSearchParams(window.location.search);
            const user = queryParams.get("user");

            if (!user) {
                setStatus('error');
                setMessage('Invalid login. Please try again.');
                setTimeout(() => navigate("/login"), 3000);
                return;
            }

            try {
                const { data } = await verifyAdminLogin(user);

                if (data.status) {
                    setStatus('success');
                    setMessage('Login successful! Redirecting...');

                    const admin_company_id = data.user.company_id;
                    // Set cookies with proper options to ensure they persist
                    Cookies.set("admin_id", data.user.id, { expires: 90, path: '/', sameSite: 'lax' });
                    Cookies.set("admin_company_id", admin_company_id, { expires: 90, path: '/', sameSite: 'lax' });
                    
                    // Store session token as fallback if cookie doesn't work (for localhost development)
                    if (data.session_token) {
                        Cookies.set("admin_session_token", data.session_token, { expires: 90, path: '/', sameSite: 'lax' }); // 90 days
                    }
                    
                    console.log("Cookies set - admin_id:", data.user.id, "admin_company_id:", admin_company_id);

                    setTimeout(() => {
                        navigate("/admin", {
                            state: { admin_company_id },
                        });
                    }, 1500);
                } else {
                    setStatus('error');
                    setMessage(data.message || 'Login failed. Please try again.');
                    setTimeout(() => navigate("/login"), 3000);
                }
            } catch (error) {
                console.error("Error verifying mobile login:", error);
                setStatus('error');
                setMessage('Server error. Please try again.');
                setTimeout(() => navigate("/login"), 3000);
            }
        };

        verifyLogin();
    }, [navigate]);

    return (
            <BackgroundBox>
                <Container maxWidth="sm">
                    <Fade in={true} timeout={500}>
                        <LoadingCard>
                            {status === 'loading' && (
                                <>
                                    <SpinnerBox>
                                        <CircularProgress 
                                            size={80} 
                                            thickness={4}
                                            sx={{
                                                color: 'primary.main',
                                            }}
                                        />
                                    </SpinnerBox>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                        Verifying Login...
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                        Please wait while we verify your identity
                                    </Typography>
                                </>
                            )}

                            {status === 'success' && (
                                <>
                                    <SuccessIcon>
                                        <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
                                    </SuccessIcon>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                        Login Successful!
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                        {message}
                                    </Typography>
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <Box sx={{ mb: 3 }}>
                                        <AdminIcon sx={{ fontSize: 64, color: 'error.main' }} />
                                    </Box>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'error.main', mb: 2 }}>
                                        Login Failed
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                        {message}
                                    </Typography>
                                </>
                            )}
                        </LoadingCard>
                    </Fade>
                </Container>
            </BackgroundBox>
    );
};

export default VerifyMobile;
